import GameConfig from "../core/data/GameConfig";
import { GameObject } from "../core/GameObject";
import { GameScene } from "../scenes/GameScene";
import { MapLayer, MapObject, MapObjectProperty, Tile, TiledMap, Tileset } from "../utils/TiledMap";
import { TileUtility } from "../utils/TileUtility";
import { CoinBag } from "./CoinBag";
import { Crate } from "./Crate";
import { Dynamite } from "./Dynamite";
import { Firecracker } from "./Firecracker";
import { Environment } from "./FlyState";
import { Ground } from "./Ground";
import { Skates } from "./Skates";
import { Trampoline } from "./Trampoline";
import { Wall } from "./Wall";

type Chunk = {
	loaded: boolean;
	key: string;
	xPos: number;
	objects: GameObject[];
};

export type EnvironmentData = {
	ground: string;
	numChunks: number;
	chunks: string[];
	movingObjectMaxOnScreen: number;
	movingObjectMinVelocityRight: number;
	movingObjectMaxVelocityRight: number;
	movingObjectMinVelocityLeft: number;
	movingObjectMaxVelocityLeft: number;
	wall: WallEnvironmentData;
}

export type WallEnvironmentData = {
	width: number;
	minVelocityToBreakThroughWall: number;
	minVelocityToEnterWall: number;
	minVelocityToExitWall: number;
};

export class ChunkSpawner {
	scene: GameScene;
	xPos = 3500; //starting position of level
	environment = 0;
	furthestEnvironmentReached = 0;
	lastEnvironmentThreshold = 0;
	chunksSpawned = 0;
	//number of chunks before spawning a wall
	groundWidth = 1017;
	groundHeight = 137;
	floorPos: number;
	maxPowerups: number;

	constructors: { [type: string]: (scene: GameScene, chunkX: number, chunkY: number, obj: MapObject, tile: Tile) => void } = {
		dynamite: this.createDynamite,
		flyingDynamite: this.createFlyingDynamite,
		crate: this.createCrate,
		flyingCrate: this.createFlyingCrate,
		coins: this.createCoins,
		coin: this.createCoin,
		skates: this.createSkates,
		flyingFirecracker: this.createFlyingFirecracker,
		firecracker: this.createFirecracker,
		coinBag: this.createCoinBag,
		trampoline: this.createTrampoline,
	};

	constructor(scene: GameScene) {
		this.scene = scene;
		this.floorPos = 1080 - this.groundHeight;
		this.maxPowerups = GameConfig.get("powerUpMaxOnScreen") as number;
	}

	update(): void {
		const xScale = this.scene.cameraController.scale;
		const x = this.scene.cameraController.camera.scrollX + 500 + this.scene.viewport.width / xScale;
		if (x > this.xPos) {
			if (this.environment == 4) {
				// ending area
				this.spawnGround(this.xPos, 1080 - this.groundHeight / 2);
				this.xPos += this.groundWidth;
			}
			else if (this.chunksSpawned >= this.numChunksInEnvironment) {
				this.chunksSpawned = 0;
				this.spawnWall();
				this.environment++;
				// if (this.environment >= 4) this.environment = 0;
				this.furthestEnvironmentReached = Math.max(this.furthestEnvironmentReached, this.environment);
			}
			else {
				const chunks = GameConfig.get<EnvironmentData[]>("environments")[this.environment].chunks;
				const isStartOrEnd = this.chunksSpawned == 0 || this.chunksSpawned == this.numChunksInEnvironment - 1;
				const chunkName = isStartOrEnd ? chunks[chunks.length - 1]: Phaser.Math.RND.pick(chunks);
				this.loadChunk(chunkName);
				this.chunksSpawned++;
			}
		}
	}

	spawnWall(): void {
		const wallData = GameConfig.get<EnvironmentData[]>("environments")[this.environment].wall;
		const width = wallData.width;
		let lastGround: Ground;
		for (let x = this.xPos; x < this.xPos + width; x += this.groundWidth) {
			lastGround = this.spawnGround(x, 1080 - this.groundHeight / 2);
		}
		const overflow = Math.max((this.xPos + width) - (lastGround.x + lastGround.width), 0);
		lastGround.cropWidth(overflow);
		this.scene.addObject(new Wall(this.scene, this.environment, this.xPos + width / 2, this.floorPos + 80), "wall");
		this.xPos += width;
	}

	loadChunk(key: string): void {
		const map = this.scene.cache.json.get(key) as TiledMap;
		const width = map.tilewidth * map.width;
		const height = map.tileheight * map.height;
		const yPos = this.floorPos - height;

		// const debugEnvBorder = this.scene.add.image(this.xPos, 0, "whiteSquare");
		// debugEnvBorder.displayHeight = 99999;
		// debugEnvBorder.tint = 0x00FF00;
		// this.scene.foregroundLayer.add(debugEnvBorder);

		for (let x = this.xPos; x < this.xPos + width + 1000; x += this.groundWidth) {
			this.spawnGround(x, 1080 - this.groundHeight / 2);
		}

		if (this.environment != Environment.ENDING) {
			for (const layer of map.layers) {
				if (layer.type == "objectgroup"){
					this.loadObjectLayer(layer, this.xPos, yPos);
				} 
			}
		}

		this.xPos += width;
	}

	spawnGround(x: number, y: number): Ground {
		const env = GameConfig.get<EnvironmentData[]>("environments")[this.environment];
		const ground = new Ground(this.scene, x, y, env.ground);
		this.scene.addObject(ground, "midBG");
		return ground;
	}

	unloadChunk(chunk: Chunk): void {
		for (const obj of chunk.objects) obj.destroy();
		chunk.objects = [];
		chunk.loaded = false;
	}

	loadObjectLayer(layer: MapLayer, chunkX: number, chunkY: number): void {
		if (layer.type != "objectgroup" || layer.objects == null) return;
		for (const obj of layer.objects) {
			const gID = TileUtility.getLocalTiledGID(obj.gid);
			const tile = this.getTile(this.scene, gID);
			let type = tile.class;
			if (tile.class == null) console.error("Null tile class - make sure tileset was saved with Tiled version 1.9.2 or higher");
			if (tile && obj.type && obj.type != "") type = obj.type; //if the object has a type, override the base tile's type
			if (tile && this.constructors[type]) {
				this.constructors[type].call(this, this.scene, chunkX, chunkY, obj, tile);
			}
		}
	}

	getTile(scene: GameScene, gid: number): Tile {
		const tileset = scene.cache.json.get("tileset") as Tileset;
		return tileset.tiles.find((t) => t.id == gid - 1) as Tile;
	}

	getObjectCenter(chunkX: number, chunkY: number, obj: MapObject): [number, number] {
		const xPos = chunkX + obj.x + obj.width / 2;
		const yPos = chunkY + obj.y - obj.width / 2;
		return [xPos, yPos];
	}

	createDynamite(scene: GameScene, chunkX: number, chunkY: number, obj: MapObject, tile: Tile) {
		if (scene.getPowerupList().length >= this.maxPowerups) return;
		const xPos = chunkX + obj.x + obj.width / 2;
		const yPos = chunkY + obj.y;
		const min = GameConfig.get<number>("specialObjectMinVelocity");
		const max = GameConfig.get<number>("specialObjectMaxVelocity");
		const vel = Phaser.Math.RND.realInRange(min, max) + scene.ltGame.player.planckBody.velocity.x;
		scene.addObject(new Dynamite(scene, xPos, yPos, true, Math.max(vel, 1)));
	}

	createFlyingDynamite(scene: GameScene, chunkX: number, chunkY: number, obj: MapObject, tile: Tile) {
		const [xPos, yPos] = this.getObjectCenter(chunkX, chunkY, obj);
		scene.addObject(new Dynamite(scene, xPos, yPos, false, 0));
	}

	createCrate(scene: GameScene, chunkX: number, chunkY: number, obj: MapObject, tile: Tile) {
		if (scene.getPowerupList().length >= this.maxPowerups) return;
		const xPos = chunkX + obj.x + obj.width / 2;
		const yPos = chunkY + obj.y;
		const min = GameConfig.get<number>("specialObjectMinVelocity");
		const max = GameConfig.get<number>("specialObjectMaxVelocity");
		const vel = Phaser.Math.RND.realInRange(min, max) + scene.ltGame.player.planckBody.velocity.x;
		scene.addObject(new Crate(scene, xPos, yPos, true, Math.max(vel, 1)));
	}

	createFlyingCrate(scene: GameScene, chunkX: number, chunkY: number, obj: MapObject, tile: Tile) {
		const [xPos, yPos] = this.getObjectCenter(chunkX, chunkY, obj);
		scene.addObject(new Crate(scene, xPos, yPos, false, 0));
	}

	createSkates(scene: GameScene, chunkX: number, chunkY: number, obj: MapObject, tile: Tile) {
		if (scene.getPowerupList().length >= this.maxPowerups) return;
		const xPos = chunkX + obj.x + obj.width / 2;
		const yPos = chunkY + obj.y - obj.height / 2;
		const min = GameConfig.get<number>("specialObjectMinVelocity");
		const max = GameConfig.get<number>("specialObjectMaxVelocity");
		const vel = Phaser.Math.RND.realInRange(min, max) + scene.ltGame.player.planckBody.velocity.x;
		scene.addObject(new Skates(scene, xPos, yPos, Math.max(vel, 1)));
	}

	createFirecracker(scene: GameScene, chunkX: number, chunkY: number, obj: MapObject, tile: Tile) {
		if (scene.getPowerupList().length >= this.maxPowerups) return;
		const xPos = chunkX + obj.x + obj.width / 2;
		const yPos = chunkY + obj.y - obj.height / 2;
		const min = GameConfig.get<number>("specialObjectMinVelocity");
		const max = GameConfig.get<number>("specialObjectMaxVelocity");
		const vel = Phaser.Math.RND.realInRange(min, max) + scene.ltGame.player.planckBody.velocity.x;
		scene.addObject(new Firecracker(scene, xPos, yPos, Math.max(vel, 1), true));
	}
	createFlyingFirecracker(scene: GameScene, chunkX: number, chunkY: number, obj: MapObject, tile: Tile){
		const [xPos, yPos] = this.getObjectCenter(chunkX, chunkY, obj);
		const min = GameConfig.get<number>("specialObjectMinVelocity");
		const max = GameConfig.get<number>("specialObjectMaxVelocity");
		const vel = Phaser.Math.RND.realInRange(min, max) + scene.ltGame.player.planckBody.velocity.x;
		scene.addObject(new Firecracker(scene, xPos, yPos, Math.max(vel, 1), false));
	}

	createTrampoline(scene: GameScene, chunkX: number, chunkY: number, obj: MapObject, tile: Tile) {
		if (scene.getPowerupList().length >= this.maxPowerups) return;
		const xPos = chunkX + obj.x + obj.width / 2;
		const yPos = chunkY + obj.y;
		const min = GameConfig.get<number>("specialObjectMinVelocity");
		const max = GameConfig.get<number>("specialObjectMaxVelocity");
		const vel = Phaser.Math.RND.realInRange(min, max) + scene.ltGame.player.planckBody.velocity.x;
		scene.addObject(new Trampoline(scene, xPos, yPos, Math.max(vel, 1)));
	}

	createCoins(scene: GameScene, chunkX: number, chunkY: number, obj: MapObject, tile: Tile) {
		if(obj.properties){
			const mapProperty = obj.properties.find((p)=> p.name == "map").value;
			if(mapProperty){
				const map = this.scene.cache.json.get(mapProperty) as TiledMap;

				const [xPos, yPos] = this.getObjectCenter(chunkX, chunkY, obj);
				for (const layer of map.layers) {
					if (layer.type == "objectgroup"){
						// const debug = scene.addObject(new GameObject(this.scene), "foreground");
						// const img = debug.addImage("whiteSquare");
						// debug.setPosition(xPos, yPos);
						// img.tint = 0xFF0000;
						const yOffset = 35;
						//map.width = how many tiles
						//map.tilewidth = width of each tile
						this.loadObjectLayer(layer, xPos - (map.tilewidth * (map.width / 2)), yPos - (map.tileheight * (map.height / 2)) - yOffset); //setting position of tile map
					} 
				}
			}
		}else{
			const [xPos, yPos] = this.getObjectCenter(chunkX, chunkY, obj);
			let width = 200;
			let step = 60;
			let oy = -width / 2;
			let everyOther = 0;
			for (let y = 0; y < 4; y++) {
				// let startX = (oy % (step * 2) == 0) ? 0 : step / 2;
				let startX = -width / 2;
				startX += (y == 0 || y == 3) ? step / 2 : 0;
				for (let ox = startX; ox <= width / 2; ox += step) {
					const coin = scene.ltGame.coinPool.get(xPos + ox, yPos + oy, everyOther % 2);
					scene.addObject(coin);
					everyOther++;
				}
				oy += step;
			}
		}

	}

	createCoin(scene: GameScene, chunkX: number, chunkY: number, obj: MapObject, tile: Tile) {
		const xPos = chunkX + obj.x + obj.width / 2;
		const yPos = chunkY + obj.y;
		const coin = scene.ltGame.coinPool.get(xPos, yPos, (xPos / tile.imagewidth + yPos / tile.imagewidth) % 2);
		scene.addObject(coin);
	}

	createCoinBag(scene: GameScene, chunkX: number, chunkY: number, obj: MapObject, tile: Tile) {
		const xPos = chunkX + obj.x + obj.width / 2;
		const yPos = chunkY + obj.y;
		scene.addObject(new CoinBag(scene, xPos, yPos));
	}

	findPropertyByName(obj: MapObject, baseTile: Tile, propName: string): MapObjectProperty | undefined {
		let property: MapObjectProperty | undefined = undefined;
		if (obj.properties) {
			property = obj.properties.find((p) => p.name === propName);
		}
		if (property === undefined && baseTile.properties) {
			property = baseTile.properties.find((p) => p.name === propName);
		}
		return property;
	}

	get numChunksInEnvironment(): number {
		return GameConfig.get<EnvironmentData[]>("environments")[this.environment].numChunks;
	}

	getEnvironmentWidth(): number {
		const offset = this.xPos;
		const wallData = GameConfig.get<EnvironmentData[]>("environments")[this.environment].wall;
		const width = (4000 * this.numChunksInEnvironment) + wallData.width;
		return width;
	}

}
